'use strict';
import { cn_duplicator } from '../model/cn_duplicator';
import { cn_element } from '../model/cn_element';
import { cn_box } from '../utils/cn_utilities';
import { cn_align_handler } from './cn_align_handler';
import { cn_camera } from './cn_camera';
import { cn_duplicator_handler } from './cn_duplicator_handler';
import { cn_event_handler } from './cn_event_handler';
import { cn_event_manager } from './cn_event_manager';
import { cn_pastille } from './cn_pastille';

//***********************************************************************************
//***********************************************************************************
//**** Edit box : a box woth various editing tools on a list of elements.
//**** contains a set of pastilles
//***********************************************************************************
//***********************************************************************************

export class cn_edit_box extends cn_event_handler {
    //***********************************************************************************
    /**
     * Constructor
     * @param {cn_event_manager} parent
     * @param {Array<cn_element>} elements
     * @param {boolean} readonly
     * @param {boolean} allow_delete
     */
    constructor(parent, elements, readonly = false, allow_delete = true) {
        super(parent);
        this.elements = elements.concat([]);

        this.selection_padding = 80;

        this._readonly = readonly;
        this._pastilles = [];
        this._common_pastilles = [];
        this._specific_pastilles = [];

        this._siblings_pastille = null;
        this._info_pastille = null;
        this._lock_pastille = null;
        this._siblings_key = '';
        this._mouseover_pastille = null;
        this._screen_box = new cn_box();

        this._duplicator_handler = null;
        this._duplicator_mode = false;

        this._align_handler = null;
        this._align_mode = false;

        let alignable_elements = elements.filter(elt => elt.is_alignable());
        if (alignable_elements.length > 1)
            this.add_align_pastille();

        if (allow_delete && elements.some(elt => elt.removable) && !this._readonly) {
            const delete_pastille = new cn_pastille([0, 0], 'delete_warn.svg');
            this._common_pastilles[5] = delete_pastille;
            delete_pastille.svg_class = 'pastille_background white';
            delete_pastille.clicked = () => {
                parent.call('delete_elements', elements);
            };
            delete_pastille.title = 'Supprimer la sélection';
            this._order_pastilles();
        }

    }

    add_pastille(pastille) {
        this._specific_pastilles.push(pastille);
        this._order_pastilles();
    }

    /**
     * Add a sibling pastille = a pastille to select all elements of the same type.
     * @param {string} key
     */
    add_select_siblings_pastille(key) {
        if (key == '') key = 'class_name';
        const element_type = this.elements[0][key];
        this._siblings_pastille = new cn_pastille([0, 0], 'cursor_default.svg');
        this._common_pastilles[1] = this._siblings_pastille;
        this._siblings_pastille.svg_class = 'pastille_background white';
        this._siblings_pastille.title = 'Sélectionner les éléments similaires';
        this._siblings_pastille.clicked = () => {
            this._parent.call('select_siblings', { element: this.elements[0], key: key });
        };
        this._siblings_pastille.visible = !this.elements.some(e => e[key] != element_type);
        this._siblings_key = key;
        this._order_pastilles();
    }

    /**
     * Add a information pastille = a pastille to display description of a type.
     * @param {string} key
     */
    add_information_pastille(key) {
        if (key == '') key = 'class_name';
        const element_type = this.elements[0][key];
        this._info_pastille = new cn_pastille([0, 0], 'information.svg');
        this._common_pastilles[0] = this._info_pastille;
        this._info_pastille.svg_class = 'pastille_background white';
        this._info_pastille.title = 'Détails du type';
        this._info_pastille.clicked = () => {
            this._parent.call('show_type_information', element_type);
        };
        this._info_pastille.visible = !this.elements.some(e => e[key] != element_type);
        this._siblings_key = key;
        this._order_pastilles();
    }

    /**
     * Add a lock pastille = a pastille to lock / unlock elements
     */
    add_lock_pastille(transaction_manager) {
        if (this._readonly) {
            return;
        }
        this._lock_pastille = new cn_pastille([0, 0], 'lock.svg');
        this._common_pastilles[4] = this._lock_pastille;
        this._lock_pastille.svg_class = 'pastille_background white';
        this._lock_pastille.title = 'Verrouiller / déverrouiller';
        this._lock_pastille.clicked = () => {
            const new_lock = !(this._lock_pastille.label == 'lock.svg');
            transaction_manager.push_transaction('Verrouillage d\'éléments', '');
            this.elements.forEach(elt => {
                transaction_manager.push_item_set(elt, ['locked']);
                elt.locked = new_lock;
            });
            this._parent.call('lock', new_lock);
        };
        this._order_pastilles();
    }

    /**
     * Add a lock pastille = a pastille to lock / unlock elements
     */
    add_duplicator_pastille() {
        if (this._readonly) {
            return;
        }
        this._duplicator_handler = null;
        this._duplicator_pastille = new cn_pastille([0, 0], 'grid.svg');
        this._common_pastilles[2] = this._duplicator_pastille;
        this._duplicator_pastille.svg_class = 'pastille_background white';
        this._duplicator_pastille.title = 'Dupliquer en grille';
        this._duplicator_pastille.clicked = () => {
            this.set_duplication_mode(true);
        };
        this._order_pastilles();
    }

    /**
     * Add a lock pastille = a pastille to lock / unlock elements
     */
    add_align_pastille() {
        if (this._readonly) {
            return;
        }
        this._align_handler = null;
        this._align_pastille = new cn_pastille([0, 0], 'align-vertical-center.png');
        this._common_pastilles[3] = this._align_pastille;
        this._align_pastille.svg_class = 'pastille_background white';
        this._align_pastille.title = 'Aligner';
        this._align_pastille.clicked = () => {
            this.set_align_mode(true);
        };
        this._order_pastilles();
    }

    remove_align_pastille() {
        this._align_handler = null;
        this._align_pastille = null;
        this._common_pastilles[3] = null;
        this._order_pastilles();
    }

    set_duplication_mode(v) {
        if (v == this._duplicator_mode) return;

        this._duplicator_mode = v;
        if (this._duplicator_mode && !this._duplicator_handler) {
            let duplicator = cn_duplicator.initiate(this.elements.concat([]));
            this._duplicator_handler = new cn_duplicator_handler(duplicator, this);
            let duplicator_edit_box = new cn_edit_box(this._duplicator_handler, duplicator.elements, false, true);
            this._duplicator_handler.set_edit_box(duplicator_edit_box);
            this._duplicator_handler.on("close", () => {
                this.set_duplication_mode(false);
                this.call("check_selection");
                return true;
            });
        }

        if (!this._duplicator_mode)
            this._duplicator_handler = null;
        this.call("duplicator");
    }

    duplication_mode() {
        return this._duplicator_mode;
    }

    set_align_mode(v) {
        if (v == this._align_mode) return;
        this.set_duplication_mode(false);
        this._align_mode = v;
        if (this._align_mode && !this._align_handler) {
            this._align_handler = new cn_align_handler(this.elements,this);
        }

        if (!this._align_mode)
            this._align_handler = null;
        this.call("align");
    }

    align_mode() {
        return this._align_mode;
    }

    //***********************************************************************************
    /**
     * Draws in svg
     * @param {cn_camera} camera
     * @returns
     */
    draw(camera) {
        var html = '';

        if (this._duplicator_pastille)
            this._duplicator_pastille.visible = !camera.is_3d();

        if (this._duplicator_mode) {
            return this._duplicator_handler.draw(camera);
        }

        if (this._align_mode) {
            return this._align_handler.draw(camera);
        }

        //*** compute bounding box */
        const screen_bb = new cn_box();
        this.elements.forEach(elt => screen_bb.enlarge_box(elt.get_screen_bounding_box(camera)));
        if (!screen_bb.posmin) return html;
        screen_bb.enlarge_distance(this.selection_padding);

        function in_x_range(x) { return (x > 0 && x < camera._width) };
        function in_y_range(y) { return (y > 0 && y < camera._height) };
        if (in_x_range(screen_bb.posmin[0]) || in_x_range(screen_bb.posmin[0] + screen_bb.size[0]) || in_y_range(screen_bb.posmin[1]) || in_y_range(screen_bb.posmin[1] + screen_bb.size[1]))
            html += `<rect class="edit_box_area" x="${screen_bb.posmin[0]}" y="${screen_bb.posmin[1]}" width="${screen_bb.size[0]}" height="${screen_bb.size[1]}" rx="15"/>`;

        //*** search for pastille list position */
        const margin = 10;

        //*** the siblings pastille is viible only if coherent element types */
        if (this._siblings_pastille) {
            const element_type = this.elements[0][this._siblings_key];
            const is_same_element = this.elements.every(e => e[this._siblings_key] == element_type);
            this._siblings_pastille.visible = is_same_element;
            if (this._info_pastille) {
                this._info_pastille.visible = is_same_element;
            }
        }

        //*** Update lock pastille */
        if (this._lock_pastille) {
            const locked = this.elements[0].locked;
            this._lock_pastille.label = (locked) ? 'lock.svg' : 'unlock.svg';
            this._lock_pastille.incoherent = this.elements.some(elt => elt.locked != locked);
        }

        const nb_visible_pastilles = this._pastilles.filter(p => p.visible).length;

        const pastille_pos = [screen_bb.posmin[0], screen_bb.posmin[1] + screen_bb.size[1]];
        const pastille_size = [camera.thumb_size * nb_visible_pastilles + margin * (nb_visible_pastilles + 1), camera.thumb_size + 2 * margin];
        if (pastille_pos[0] < camera.padding[3])
            pastille_pos[0] = camera.padding[3];
        if (pastille_pos[0] + pastille_size[0] > camera._width - camera.padding[1])
            pastille_pos[0] = camera._width - camera.padding[1] - pastille_size[0];

        if (pastille_pos[1] < camera.padding[0])
            pastille_pos[1] = camera.padding[0];
        if (pastille_pos[1] + pastille_size[1] > camera._height - camera.padding[2])
            pastille_pos[1] = camera._height - camera.padding[2] - pastille_size[1];
        const pastille_world = camera.screen_to_world(pastille_pos);

        this._screen_box = new cn_box();
        this._screen_box.posmin = pastille_pos;
        this._screen_box.size = pastille_size;

        if (this._pastilles.length) {
            html += `<rect class="edit_box_background" x="${pastille_pos[0]}" y="${pastille_pos[1]}" width="${pastille_size[0]}" height="${pastille_size[1]}"/>`;
        }

        //*** Draw pastilles */
        var nb_p = 0;
        for (var index = 0; index < this._pastilles.length; index++) {
            if (!this._pastilles[index].visible) continue;
            this._pastilles[index].rectangle = [camera.thumb_size, camera.thumb_size];
            this._pastilles[index].position = pastille_world;
            this._pastilles[index].offset = [margin + camera.thumb_size / 2 + camera.thumb_size * nb_p + margin * nb_p, margin + camera.thumb_size / 2];
            html += this._pastilles[index].draw(camera);
            nb_p++;
        }
        return html;
    }

    clear_move() {
        this._mouseover_pastille = null;
        this._pastilles.forEach(p => p.mouseover = false);
        if (this._duplicator_mode)
            this._duplicator_handler.clear_move();
        if (this._align_mode)
            this._align_handler.clear_move();
    }

    move(mouse_event) {
        this._mouseover_pastille = null;

        if (this._duplicator_mode)
            return this._duplicator_handler.move(mouse_event);

        if (this._align_mode)
            return this._align_handler.move(mouse_event);

        this._pastilles.forEach(p => {
            p.mouseover = p.visible && p.contains(mouse_event.mouse_world, mouse_event.camera);
            if (p.mouseover) {
                this._mouseover_pastille = p;
                mouse_event.title = p.title;
            }
        });
        if (this._mouseover_pastille != null) return true;
        return this._screen_box.contains_point(mouse_event.mouse_screen);
    }

    grab(mouse_event) {
        if (this._duplicator_mode)
            return this._duplicator_handler.grab(mouse_event);
        
        if (this._align_mode)
            return this._align_handler.grab(mouse_event);

        return this.move(mouse_event);
    }

    drag(mouse_event) {
        if (this._duplicator_mode)
            return this._duplicator_handler.drag(mouse_event);
        
        if (this._align_mode)
            return this._align_handler.drag(mouse_event);

        return false;
    }

    click(mouse_event) {
        if (this._duplicator_mode)
            return this._duplicator_handler.click(mouse_event);
        
        if (this._align_mode)
            return this._align_handler.click(mouse_event);

        if (!this._mouseover_pastille) return false;
        this._mouseover_pastille.clicked();
        return true;
    }

    _order_pastilles() {
        this._pastilles = [...this._specific_pastilles, ...this._common_pastilles.filter(p => !!p)];
    }

}

